// Keyed on the person too: sessionStorage outlives a logout, and "completed" can mean "not an admin here".

import { STORAGE_KEYS } from "../constants";
import { User } from "../models/User";
import secureLocalStorage from "./secureLocalStorage";

const COMPLETED_PREFIX = "cc:onboarding-completed:";
const DISMISSED_PREFIX = "cc:onboarding-dismissed:";

// A read before the session hydrates gets its own bucket rather than sharing one with every signed-out reader.
const principal = (): string => {
  try {
    return secureLocalStorage.getJSON<User>(STORAGE_KEYS.USER)?.refId ?? "anon";
  } catch {
    return "anon";
  }
};

const keyFor = (prefix: string, organizationRef: string): string => `${prefix}${principal()}:${organizationRef}`;

// sessionStorage throws outright in some privacy modes, so every access fails soft to "not recorded".
const read = (key: string): boolean => {
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const write = (key: string): void => {
  try {
    sessionStorage.setItem(key, "1");
  } catch {
    // Storage unavailable — the worst case is asking the server again, so there is nothing to recover.
  }
};

export const isOnboardingCompletedCached = (organizationRef: string): boolean =>
  read(keyFor(COMPLETED_PREFIX, organizationRef));

export const cacheOnboardingCompleted = (organizationRef: string): void =>
  write(keyFor(COMPLETED_PREFIX, organizationRef));

export const isReminderDismissed = (organizationRef: string): boolean =>
  read(keyFor(DISMISSED_PREFIX, organizationRef));

export const dismissReminder = (organizationRef: string): void => write(keyFor(DISMISSED_PREFIX, organizationRef));
